import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { useMemo, useState } from "react";

import {
  bothIcon,
  budgetIcon,
  checkShield,
  clubIcon,
  clubsIcon,
  crossCircle,
  filterIcon,
  flagIcon,
  groupIcon,
  leagueIcon,
  marketIcon,
  metalIcon,
  pastAndPresentIcon,
  rarityIcon,
} from "../../../../../designSystemIcons";

export type ActiveFilter = {
  key: FilterKey;
  value: string;
};

export const filterIcons = {
  budget: budgetIcon,
  country: flagIcon,
  league: leagueIcon,
  clubs: clubsIcon,
  club: clubIcon,
  both: bothIcon,
  market: marketIcon,
  playerFilter: filterIcon,
  rarity: rarityIcon,
  pastAndPresentClub: pastAndPresentIcon,
  metal: metalIcon,
  group: groupIcon,
} as const;

export type FilterKey = keyof typeof filterIcons;

type Props = {
  filters: ActiveFilter[];
  onClearFilter: (key: FilterKey) => void;
  disabled?: boolean;
  includedPlayers?: number;
};

const iconDict = {
  budget: budgetIcon,
  both: bothIcon,
  club: clubIcon,
  playerFilter: filterIcon,
  country: flagIcon,
  league: leagueIcon,
  clubs: clubsIcon,
  rarity: rarityIcon,
  pastAndPresentClub: pastAndPresentIcon,
  metal: metalIcon,
  market: marketIcon,
  group: groupIcon,
};

export const TopFilterBar = ({
  filters,
  onClearFilter,
  disabled,
  includedPlayers,
}: Props) => {
  const { t } = useTranslation();
  const [hoverFilter, setHoverFilter] = useState(-1);
  const [hasFilters, setHasFilters] = useState(false);

  const groupedFilters = useMemo(() => {
    const groupedFilters = filters.reduce((acc, filter) => {
      if (
        filter.key === "both" ||
        filter.key === "club" ||
        filter.key === "market"
      ) {
        acc.push({ ...filter, value: filter.key });
      } else if (filter.key === "budget") {
        acc.push(filter);
      } else if (filter.key === "playerFilter") {
        acc.push({ ...filter });
      } else {
        const existingFilter = acc.find((f) => f.key === "group");
        if (existingFilter) {
          existingFilter.value += Number(filter.value);
        } else {
          acc.push({
            key: filter.key,
            value: Number(filter.value),
          });
        }
      }
      return acc;
    }, []);

    setHasFilters(groupedFilters.length > 0);

    return groupedFilters;
  }, [filters]);

  return (
    <div className="h-[62px]">
      <div
        className={cn(
          "flex items-center justify-center px-1 rounded-lg sb-v2-lg:rounded-lg py-1 sb-v2-lg:py-2 h-full",
          !hasFilters && !disabled && "hidden",
          disabled
            ? "bg-surface text-foreground-muted"
            : "border bg-surface-raised border-edge-strong desktop:rounded-none text-foreground-body",
        )}
      >
        {!hasFilters ? (
          <p className="text-foreground-muted text-sm">
            {t("noFiltersApplied")}
          </p>
        ) : (
          groupedFilters.map((filter, index) => (
            <button
              key={filter.key}
              className={cn(
                "relative flex flex-col min-w-10 rounded",
                disabled || filter.key === "market"
                  ? "cursor-default"
                  : "hover:bg-control",
              )}
              onMouseEnter={() => setHoverFilter(index)}
              onMouseLeave={() => setHoverFilter(-1)}
            >
              {hoverFilter === index &&
                !disabled &&
                filter.key !== "market" && (
                  <div className="absolute right-0 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2">
                    <div
                      className="w-5 h-5 rounded-full bg-white/75"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearFilter(filter.key);
                        setHoverFilter(-1);
                      }}
                    >
                      {crossCircle}
                    </div>
                  </div>
                )}
              <div className="w-6 h-6 mx-auto">
                {iconDict[filter.key] || groupIcon}
              </div>
              <div
                className={cn(
                  "mx-auto font-medium",
                  disabled ? "text-tiny" : "text-sm",
                )}
              >
                {filter.value}
              </div>
            </button>
          ))
        )}
        {disabled && includedPlayers > 0 && (
          <div className="flex flex-col w-10 rounded">
            <div className="w-6 h-6 mx-auto">{checkShield}</div>
            <div className="mx-auto text-tiny font-medium">
              {includedPlayers}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
